/**
 * <search-results-component>
 *
 * Search results page (sections/main-search-results.liquid), following the Horizon theme approach:
 * - applying filters, sorting and paging re-render the section through the Section Rendering API
 *   (`?section_id=<id>`) instead of reloading the page
 * - the URL is kept in sync with history.pushState so results can be shared and the back button works
 * - all events are delegated from the component, so re-rendered markup needs no re-binding
 *
 * Interaction matches the collection page:
 * - desktop: the overlay shows and the filter sidebar slides in from the left (collection-redesign.css)
 * - mobile: the filter sheet (#myNav) slides up from the bottom (filter-css.css)
 * - filter choices are applied with "APPLY FILTERS"; sorting applies straight away
 * - "Discover more" loads the next page into the grid, and does so automatically on scroll
 * - once scrolled into the results, <body> gets 'scroll-body' so the Filter / Sort bar sticks to the
 *   top on mobile and Filters floats on desktop (collection-redesign.css / collection-redesign.js)
 */

const INFINITE_SCROLL_MARGIN = '0px 0px 1200px 0px';
const SKELETON_COUNT = 6;
const SWATCH_DRAG_THRESHOLD = 5;
// Same thresholds as collection-redesign.js: sticky after 680px, released within 1850px of the page end.
const STICKY_TOOLBAR_START = 680;
const STICKY_TOOLBAR_END_GAP = 1850;

const SKELETON_CARD = `
  <div class="col-sm-4 col-xs-6 skeleton-card search-results__skeleton">
    <div class="product-item__skeleton">
      <div class="skeleton-img skeleton-shimmer-plp"></div>
      <div class="skeleton-bottom">
        <div class="skeleton-line skeleton-shimmer-plp skeleton-title"></div>
        <div class="skeleton-price-star-row">
          <div class="skeleton-line skeleton-shimmer-plp skeleton-price"></div>
          <div class="skeleton-line skeleton-shimmer-plp skeleton-stars"></div>
        </div>
        <div class="skeleton-line skeleton-shimmer-plp skeleton-swatches-row"></div>
      </div>
    </div>
  </div>`;

class SearchResultsComponent extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {IntersectionObserver | null} */
  #loadMoreObserver = null;
  #isLoadingMore = false;
  /** @type {{ row: HTMLElement, startX: number, startScroll: number, dragged: boolean } | null} */
  #swatchDrag = null;
  #stickyFrame = 0;

  /**
   * Elements marked with `ref="name"`, looked up fresh because the markup is replaced on every update.
   * @returns {Record<string, HTMLElement | undefined>}
   */
  get refs() {
    const refs = {};
    for (const element of this.querySelectorAll('[ref]')) {
      refs[element.getAttribute('ref')] = element;
    }
    return refs;
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('input', this.#onInput);
    this.addEventListener('submit', this.#onSubmit);
    this.addEventListener('toggle', this.#onToggle, true);
    this.addEventListener('pointerdown', this.#onSwatchPointerDown);
    this.addEventListener('click', this.#onSwatchClickCapture, true);
    window.addEventListener('popstate', this.#onPopState);
    window.addEventListener('resize', this.#onResize);
    window.addEventListener('scroll', this.#onScroll, { passive: true });

    this.#afterRender({ initial: true });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('input', this.#onInput);
    this.removeEventListener('submit', this.#onSubmit);
    this.removeEventListener('toggle', this.#onToggle, true);
    this.removeEventListener('pointerdown', this.#onSwatchPointerDown);
    this.removeEventListener('click', this.#onSwatchClickCapture, true);
    window.removeEventListener('popstate', this.#onPopState);
    window.removeEventListener('resize', this.#onResize);
    window.removeEventListener('scroll', this.#onScroll);
    cancelAnimationFrame(this.#stickyFrame);

    this.#endSwatchDrag();
    this.#loadMoreObserver?.disconnect();
    this.#abortController?.abort();
    document.body.classList.remove('mob-filter-open', 'scroll-body');
  }

  /* Event handlers */

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const actionElement = target.closest('[data-action]');
    if (actionElement) {
      this.#runAction(actionElement);
      return;
    }

    const filterLink = /** @type {HTMLAnchorElement | null} */ (target.closest('a[data-search-filter-link]'));
    if (filterLink) {
      event.preventDefault();
      event.stopPropagation();
      this.#update(filterLink.href);
      return;
    }

    const loadMore = target.closest('[ref="loadMore"]');
    if (loadMore) {
      event.preventDefault();
      this.#loadMore();
      return;
    }

    const swatch = target.closest('.variant_img_color_custom');
    if (swatch) this.#onSwatchClick(/** @type {HTMLElement} */ (swatch));
  };

  /** Buttons that aren't real <button>s (copied from the collection markup) work with the keyboard too. */
  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape') {
      this.closeFilters();
      return;
    }

    const target = /** @type {HTMLElement} */ (event.target);
    if ((event.key === 'Enter' || event.key === ' ') && target.matches('[data-action][role="button"]')) {
      event.preventDefault();
      this.#runAction(target);
    }
  };

  /** @param {Element} element */
  #runAction(element) {
    switch (element.getAttribute('data-action')) {
      case 'open-filters':
        this.openDesktopFilters();
        break;
      case 'open-filters-mobile':
        this.openMobileFilters();
        break;
      case 'close-filters':
        this.closeFilters();
        break;
      case 'apply-filters': {
        const form = this.#formFor(element);
        if (form) this.#update(this.#buildFilterUrl(form));
        break;
      }
    }
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = /** @type {HTMLInputElement | HTMLSelectElement} */ (event.target);

    if (target.matches('[data-sort-select]')) {
      // theme.js listens for changes on `.advanced-sortby .field` at <body> and runs its own
      // collection-only AJAX sort. The collection page stops the event too.
      event.stopPropagation();
      this.#update(this.#buildSortUrl(target.value));
      return;
    }

    const form = this.#formFor(target);
    if (!form) return;

    if (target.matches('[data-price-slider]')) this.#syncPriceInputsFromSliders(form, target);
    if (target.matches('[data-price-input]')) this.#syncPriceSlidersFromInputs(form, target);
    this.#updateApplyState(form);
  };

  /** Keep the price text inputs in step while a slider is being dragged. */
  /** @param {Event} event */
  #onInput = (event) => {
    const target = /** @type {HTMLInputElement} */ (event.target);
    if (!target.matches('[data-price-slider]')) return;
    const form = this.#formFor(target);
    if (form) this.#syncPriceInputsFromSliders(form, target);
  };

  /** Enter in a price field applies the filters, like the Apply button. */
  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    const form = /** @type {HTMLFormElement} */ (event.target);
    if (!form.matches('[data-filters-form]')) return;
    event.preventDefault();
    this.#update(this.#buildFilterUrl(form));
  };

  /** On mobile only one filter group is open at a time, like the collection page. */
  /** @param {Event} event */
  #onToggle = (event) => {
    const details = /** @type {HTMLDetailsElement} */ (event.target);
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;

    const form = details.closest('[data-filters-form="mobile"]');
    if (!form) return;
    for (const other of form.querySelectorAll('details.filter-group[open]')) {
      if (other !== details) other.removeAttribute('open');
    }
  };

  #onPopState = () => {
    this.#update(window.location.href, { pushState: false });
  };

  #onResize = () => {
    this.#sizeDesktopSortSelect();
    this.#markDraggableSwatchRows();
    this.#updateStickyToolbar();
  };

  #onScroll = () => {
    if (this.#stickyFrame) return;
    this.#stickyFrame = requestAnimationFrame(() => {
      this.#stickyFrame = 0;
      this.#updateStickyToolbar();
    });
  };

  /**
   * Sticky Filter / Sort bar, as on the collection page: <body> gets 'scroll-body' once the shopper
   * is 680px down and more than 1850px from the bottom. collection-redesign.css then fixes
   * .custom-filter-container to the top on mobile and floats the Filters button on desktop.
   */
  #updateStickyToolbar() {
    const hasToolbar = Boolean(this.querySelector('.custom-filter-container'));
    const scroll = window.scrollY;
    const distanceToEnd = document.body.scrollHeight - scroll;
    const sticky = hasToolbar && scroll > STICKY_TOOLBAR_START && distanceToEnd > STICKY_TOOLBAR_END_GAP;
    document.body.classList.toggle('scroll-body', sticky);
  }

  /* Filter drawers */

  openDesktopFilters() {
    this.refs.overlay?.classList.remove('hide');
    this.querySelector('[data-action="open-filters"]')?.setAttribute('aria-expanded', 'true');
    this.refs.desktopDrawer?.focus({ preventScroll: true });
  }

  openMobileFilters() {
    const drawer = this.refs.mobileDrawer;
    if (!drawer) return;
    drawer.style.transform = 'translateY(0)';
    document.body.classList.add('mob-filter-open');
    this.querySelector('[data-action="open-filters-mobile"]')?.setAttribute('aria-expanded', 'true');
    drawer.focus({ preventScroll: true });
  }

  closeFilters() {
    const overlay = this.refs.overlay;
    if (overlay && !overlay.classList.contains('hide')) {
      overlay.classList.add('hide');
      const toggle = /** @type {HTMLElement | null} */ (this.querySelector('[data-action="open-filters"]'));
      toggle?.setAttribute('aria-expanded', 'false');
      toggle?.focus({ preventScroll: true });
    }

    const drawer = this.refs.mobileDrawer;
    if (drawer && document.body.classList.contains('mob-filter-open')) {
      drawer.style.transform = 'translateY(100%)';
      document.body.classList.remove('mob-filter-open');
      const toggle = /** @type {HTMLElement | null} */ (this.querySelector('[data-action="open-filters-mobile"]'));
      toggle?.setAttribute('aria-expanded', 'false');
      toggle?.focus({ preventScroll: true });
    }
  }

  /**
   * The Apply button turns black once the filter choices differ from what's applied,
   * and on mobile the Apply bar appears (collection page behaviour).
   * @param {HTMLFormElement} form
   */
  #updateApplyState(form) {
    const changed = this.#normalise(this.#buildFilterUrl(form)) !== this.#normalise(window.location.href);
    const drawer = form.closest('#myNav, #sidebar.new-template-counter-desktop');
    const hasActiveFilters = Boolean(this.querySelector('.filter-wrapper:not(.hide)'));

    if (form.dataset.filtersForm === 'mobile') {
      this.refs.mobileActions?.classList.toggle('hide', !changed && !hasActiveFilters);
    } else {
      drawer?.querySelector('.filter-apply-button')?.classList.toggle('bg-black', changed);
    }
  }

  /** @param {Element} element */
  #formFor(element) {
    const drawer = element.closest('#myNav, #sidebar.new-template-counter-desktop');
    return /** @type {HTMLFormElement | null} */ (drawer?.querySelector('[data-filters-form]') ?? null);
  }

  /* Price range */

  /**
   * @param {HTMLFormElement} form
   * @param {HTMLInputElement} slider
   */
  #syncPriceInputsFromSliders(form, slider) {
    const minSlider = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-price-slider="min"]'));
    const maxSlider = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-price-slider="max"]'));
    const minInput = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-price-input="min"]'));
    const maxInput = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-price-input="max"]'));
    if (!minSlider || !maxSlider || !minInput || !maxInput) return;

    // Stop the handles from crossing over.
    if (Number(minSlider.value) > Number(maxSlider.value)) {
      if (slider === minSlider) minSlider.value = maxSlider.value;
      else maxSlider.value = minSlider.value;
    }

    minInput.value = minSlider.value === '0' ? '' : minSlider.value;
    maxInput.value = maxSlider.value === maxSlider.max ? '' : maxSlider.value;
  }

  /**
   * @param {HTMLFormElement} form
   * @param {HTMLInputElement} input
   */
  #syncPriceSlidersFromInputs(form, input) {
    const slider = /** @type {HTMLInputElement | null} */ (
      form.querySelector(`[data-price-slider="${input.dataset.priceInput}"]`)
    );
    const max = Number(input.dataset.max) || Number(slider?.max) || 0;
    const parsed = parseInt(input.value.replace(/[^\d]/g, ''), 10);
    const value = Number.isNaN(parsed) ? null : Math.min(Math.max(parsed, 0), max);

    input.value = value === null ? '' : String(value);
    if (slider) slider.value = value === null ? (input.dataset.priceInput === 'min' ? '0' : slider.max) : String(value);
  }

  /* URLs */

  /**
   * Search URL from a filter form, keeping the current sort.
   * @param {HTMLFormElement} form
   */
  #buildFilterUrl(form) {
    const params = new URLSearchParams(/** @type {any} */ (new FormData(form)));

    // Leave out price bounds that match the full range so they don't count as active filters.
    const minInput = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-price-input="min"]'));
    const maxInput = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-price-input="max"]'));
    if (minInput && (minInput.value === '' || Number(minInput.value) <= 0)) params.delete(minInput.name);
    if (maxInput && (maxInput.value === '' || Number(maxInput.value) >= Number(maxInput.dataset.max))) {
      params.delete(maxInput.name);
    }

    const sort = new URLSearchParams(window.location.search).get('sort_by');
    if (sort) params.set('sort_by', sort);

    return `${window.location.pathname}?${params.toString()}`;
  }

  /**
   * Current search URL (applied filters only) with a new sort.
   * @param {string} sortValue
   */
  #buildSortUrl(sortValue) {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    if (sortValue && sortValue !== this.dataset.defaultSort) url.searchParams.set('sort_by', sortValue);
    else url.searchParams.delete('sort_by');
    return `${url.pathname}?${url.searchParams.toString()}`;
  }

  /** Compare URLs by their query, ignoring order and paging. */
  /** @param {string} url */
  #normalise(url) {
    const params = new URL(url, window.location.origin).searchParams;
    params.delete('page');
    params.delete('section_id');
    return Array.from(params.entries())
      .map(([key, value]) => `${key}=${value}`)
      .sort()
      .join('&');
  }

  /** @param {string} url */
  #sectionUrl(url) {
    const sectionUrl = new URL(url, window.location.origin);
    sectionUrl.searchParams.set('section_id', this.sectionId);
    return sectionUrl.toString();
  }

  /* Rendering */

  /**
   * Fetch the section for `url` and swap the component's content.
   * @param {string} url
   * @param {{ pushState?: boolean }} [options]
   */
  async #update(url, { pushState = true } = {}) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.toggleAttribute('loading', true);

    try {
      const response = await fetch(this.#sectionUrl(url), { signal: this.#abortController.signal });
      if (!response.ok) throw new Error(`Search results failed with status ${response.status}`);

      const html = await response.text();
      const nextComponent = new DOMParser().parseFromString(html, 'text/html').querySelector('search-results-component');
      if (!nextComponent) throw new Error('Search results section not found in response');

      // New markup arrives with the drawers closed, as on the collection page after filtering.
      document.body.classList.remove('mob-filter-open');
      this.innerHTML = nextComponent.innerHTML;

      if (pushState) {
        const pageUrl = new URL(url, window.location.origin);
        pageUrl.searchParams.delete('section_id');
        history.pushState({ searchResults: true }, '', pageUrl.toString());
      }

      this.#afterRender();
      this.#announce();
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error);
      // Fall back to a normal page load so the shopper still gets their results.
      window.location.href = url;
    } finally {
      this.toggleAttribute('loading', false);
    }
  }

  #announce() {
    const status = this.refs.status;
    const count = this.querySelector('.new-template-counter-desktop .view-count')?.textContent?.replace(/\s+/g, ' ').trim();
    if (status) status.textContent = count || 'No products found';
  }

  /* Discover more / infinite scroll */

  async #loadMore() {
    const { loadMore, grid } = this.refs;
    const nextUrl = loadMore?.getAttribute('href');
    if (!loadMore || !grid || !nextUrl || this.#isLoadingMore) return;

    this.#isLoadingMore = true;
    loadMore.setAttribute('aria-busy', 'true');
    grid.insertAdjacentHTML('beforeend', SKELETON_CARD.repeat(SKELETON_COUNT));

    try {
      const response = await fetch(this.#sectionUrl(nextUrl));
      if (!response.ok) throw new Error(`Load more failed with status ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextGrid = doc.querySelector('[ref="grid"]');
      const nextLoadMore = doc.querySelector('[ref="loadMore"]');

      grid.querySelectorAll('.search-results__skeleton').forEach((skeleton) => skeleton.remove());
      if (nextGrid) {
        const fragment = document.createDocumentFragment();
        fragment.append(...Array.from(nextGrid.children));
        grid.append(fragment);
      }

      if (nextLoadMore) {
        loadMore.setAttribute('href', nextLoadMore.getAttribute('href') ?? '');
      } else {
        this.refs.loadMoreWrapper?.remove();
        this.#loadMoreObserver?.disconnect();
      }

      this.#afterRender({ appended: true });
    } catch (error) {
      console.error(error);
      grid.querySelectorAll('.search-results__skeleton').forEach((skeleton) => skeleton.remove());
    } finally {
      this.#isLoadingMore = false;
      loadMore.removeAttribute('aria-busy');
      // If the new batch still leaves the button on screen, keep loading.
      this.#observeLoadMore();
    }
  }

  #observeLoadMore() {
    this.#loadMoreObserver?.disconnect();
    const loadMore = this.refs.loadMore;
    if (!loadMore || !('IntersectionObserver' in window)) return;

    this.#loadMoreObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.#loadMore();
      },
      { rootMargin: INFINITE_SCROLL_MARGIN }
    );
    this.#loadMoreObserver.observe(loadMore);
  }

  /* After every render */

  /** @param {{ appended?: boolean, initial?: boolean }} [options] */
  #afterRender({ appended = false, initial = false } = {}) {
    this.#updateViewCount();
    this.#applyDeliveryMessaging();
    this.#markDraggableSwatchRows();
    if (!appended) {
      this.#sizeDesktopSortSelect();
      this.#observeLoadMore();
    }
    this.#updateStickyToolbar();

    // Theme scripts (quick shop, wishlist, notify me) re-initialise product cards on this event,
    // the same one the collection page fires after filtering. Cards in the first page load are
    // already initialised by those scripts.
    if (!initial) document.dispatchEvent(new Event('filterAction'));
  }

  /** The desktop sort <select> sits over the "Sort" label and takes its width (collection page behaviour). */
  #sizeDesktopSortSelect() {
    const select = /** @type {HTMLElement | null} */ (this.querySelector('[data-sort-select="desktop"]'));
    const label = /** @type {HTMLElement | null} */ (this.querySelector('.new-template-counter-desktop .sort-icon'));
    if (select && label && label.offsetWidth) select.style.width = `${label.offsetWidth}px`;
  }

  #updateViewCount() {
    const shown = this.refs.grid?.querySelectorAll('.product-item').length;
    if (shown === undefined) return;

    const page = Number(new URL(window.location.href).searchParams.get('page') || 1);
    const offset = (page - 1) * (Number(this.dataset.pageSize) || 0);
    for (const element of [this.refs.viewCount, this.refs.viewCountMobile, this.refs.viewCountBottom]) {
      if (element) element.textContent = String(offset + shown);
    }
  }

  /** Same rule as the collection page: hide standard delivery after Friday 2pm and at weekends. */
  #applyDeliveryMessaging() {
    const now = new Date();
    const day = now.getDay();
    const isWeekendCutoff = (day === 5 && now.getHours() >= 14) || day === 6 || day === 0;
    const selector = isWeekendCutoff ? '.standard' : '.standard-fallback';
    this.querySelectorAll(selector).forEach((element) => element.classList.add('hide'));
  }

  /* Product card colour swatches (delegated so they work on cards added by filtering and load more) */

  /**
   * Rows with more colours than fit the card scroll sideways. Touch and trackpads scroll natively;
   * mouse users can drag the row, like the slick slider on the collection page.
   */
  #markDraggableSwatchRows = () => {
    for (const row of this.querySelectorAll('.product-grid .color_variant')) {
      row.toggleAttribute('data-draggable', row.scrollWidth > row.clientWidth + 1);
    }
  };

  /** @param {PointerEvent} event */
  #onSwatchPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const row = /** @type {HTMLElement | null} */ (
      /** @type {HTMLElement} */ (event.target).closest('.color_variant[data-draggable]')
    );
    if (!row) return;

    this.#swatchDrag = { row, startX: event.clientX, startScroll: row.scrollLeft, dragged: false };
    window.addEventListener('pointermove', this.#onSwatchPointerMove);
    window.addEventListener('pointerup', this.#onSwatchPointerUp);
    window.addEventListener('pointercancel', this.#onSwatchPointerUp);
  };

  /** @param {PointerEvent} event */
  #onSwatchPointerMove = (event) => {
    const drag = this.#swatchDrag;
    if (!drag) return;

    const distance = event.clientX - drag.startX;
    if (!drag.dragged && Math.abs(distance) < SWATCH_DRAG_THRESHOLD) return;

    drag.dragged = true;
    drag.row.setAttribute('data-dragging', '');
    drag.row.scrollLeft = drag.startScroll - distance;
    event.preventDefault();
  };

  #onSwatchPointerUp = () => {
    const drag = this.#swatchDrag;
    this.#endSwatchDrag();
    // Keep the dragged state until the click that follows pointerup has been swallowed.
    if (drag?.dragged) setTimeout(() => drag.row.removeAttribute('data-dragging'), 0);
  };

  #endSwatchDrag() {
    this.#swatchDrag = null;
    window.removeEventListener('pointermove', this.#onSwatchPointerMove);
    window.removeEventListener('pointerup', this.#onSwatchPointerUp);
    window.removeEventListener('pointercancel', this.#onSwatchPointerUp);
  }

  /** A drag shouldn't also count as a click on the swatch or product link under the pointer. */
  /** @param {MouseEvent} event */
  #onSwatchClickCapture = (event) => {
    const row = /** @type {HTMLElement} */ (event.target).closest?.('.color_variant[data-dragging]');
    if (!row) return;
    event.preventDefault();
    event.stopPropagation();
  };

  /** @param {HTMLElement} swatch */
  async #onSwatchClick(swatch) {
    const card = swatch.closest('.product-content-wrapper');
    if (!card) return;

    let imageUrl = swatch.getAttribute('data-href');
    const handle = swatch.getAttribute('data-handle');

    if (!imageUrl && handle) {
      try {
        const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}products/${handle}.js`);
        imageUrl = (await response.json()).featured_image;
      } catch (error) {
        console.error(error);
      }
    }

    if (!imageUrl) return;
    card.querySelectorAll('.mpt-image').forEach((image) => {
      image.setAttribute('src', imageUrl);
      image.setAttribute('srcset', imageUrl);
    });
  }
}

if (!customElements.get('search-results-component')) {
  customElements.define('search-results-component', SearchResultsComponent);
}
